import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants/hooks'
import type {
  HookParams,
  RentCar,
  RentCarAvailableData,
  RentCarBookingHistoryParams,
  RentCarParams,
} from '@/libs/interfaces'
import { rentCarAPI } from '@/services/api'

export function useRentCarsCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rentCarAPI.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.rentCar.all,
      })
      toast.success(getCreatedToastMessage(result, t('rentalFleet.vehicleAdded')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
export function useRentCarsDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rentCarAPI.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.rentCar.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
export function useRentCarsUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rentCarAPI.update,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.rentCar.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useRentCars({ params, enabled }: HookParams<RentCarParams>) {
  return useQuery<RentCarAvailableData>({
    queryKey: QUERY_KEYS.rentCar.available(params),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await rentCarAPI.getAvailableRentCars(params)
    },
    staleTime: 0, // availability changes with each booking — always refetch
  })
}

export function useRentCarsByRegNum({ params, enabled }: HookParams<RentCar>) {
  return useQuery<RentCar>({
    queryKey: QUERY_KEYS.rentCar.regNumber(params?.rentCarRegNum || ''),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await rentCarAPI.getByRegNum(params)
    },
    staleTime: STALE_TIME['24Hours'], // 24 hour
  })
}

export function useRentCarsInfinite({ params, enabled }: HookParams<RentCarParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.rentCar.infinite(params),
    queryFn: ({ pageParam }) =>
      rentCarAPI.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useRentCarsBookingHistoryInfinite({
  params,
  enabled,
}: HookParams<RentCarBookingHistoryParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.rentCar.historyInfinite(params),
    queryFn: ({ pageParam }) =>
      rentCarAPI.listHistoryCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}
